import React, { useState, useEffect } from 'react';
import { userService } from '@/application/services';
import { User, UserRecentOrder } from '@/domain/entities';
import { Pagination } from '../components/Pagination';
import { UsersTable } from '../components/UsersTable';
import { UsersFilters } from '../components/UsersFilters';
import { HttpError } from '@/domain/entities/ApiError';

type HasOrdersFilter = 'all' | 'with' | 'without';

const DEFAULT_LIMIT = 20;

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hasOrdersFilter, setHasOrdersFilter] = useState<HasOrdersFilter>('all');
  const [limit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<User | null>(null);
  const [recentOrders, setRecentOrders] = useState<UserRecentOrder[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, search, hasOrdersFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { limit: number; offset: number; search?: string; hasOrders?: boolean } = {
        limit,
        offset,
      };

      if (search.trim()) {
        filters.search = search.trim();
      }

      if (hasOrdersFilter === 'with') {
        filters.hasOrders = true;
      } else if (hasOrdersFilter === 'without') {
        filters.hasOrders = false;
      }

      const response = await userService.getUsers(filters);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить пользователей');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = () => {
    setOffset(0);
    setSearch(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setHasOrdersFilter('all');
    setOffset(0);
  };

  const handlePageChange = (newOffset: number) => {
    setOffset(newOffset);
  };

  const handleOpenUser = async (user: User) => {
    setSelectedUserId(user.id);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await userService.getUser(user.id, 5);
      setUserDetail(response.user);
      setRecentOrders(response.recentOrders);
    } catch (err) {
      if (err instanceof HttpError) {
        setDetailError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setDetailError(err.message);
      } else {
        setDetailError('Не удалось загрузить карточку пользователя');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteTarget({ id: user.id, label: user.name || user.email });
  };

  const handleCloseDetail = () => {
    setSelectedUserId(null);
    setUserDetail(null);
    setRecentOrders([]);
    setDetailError(null);
    setDeleteLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await userService.deleteUser(deleteTarget.id);
      if (selectedUserId === deleteTarget.id) {
        handleCloseDetail();
      }
      setDeleteTarget(null);
      await loadUsers();
    } catch (err) {
      const message = err instanceof HttpError ? err.data?.error || err.message : err instanceof Error ? err.message : 'Не удалось удалить пользователя';
      if (selectedUserId === deleteTarget.id) {
        setDetailError(message);
      } else {
        setError(message);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Пользователи</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Управление пользователями магазина • Всего: {total}
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="flex-shrink-0 mt-4 border-t border-gray-200 dark:border-gray-700"></div>

      {/* Filters */}
      <div className="flex-shrink-0 mt-6">
        <UsersFilters
          searchInput={searchInput}
          hasOrdersFilter={hasOrdersFilter}
          onSearchChange={setSearchInput}
          onHasOrdersChange={setHasOrdersFilter}
          onSubmit={handleSearchSubmit}
          onReset={handleReset}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Table with Pagination */}
      <div className="flex-1 flex flex-col min-h-0 mt-4">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Загрузка...</div>
        ) : (
          <>
            <div className="flex-1 overflow-auto min-h-0">
              <UsersTable
                users={users}
                onViewDetails={handleOpenUser}
                onDelete={handleDeleteClick}
              />
            </div>
            <div className="flex-shrink-0 mt-4">
              <Pagination
                total={total}
                limit={limit}
                offset={offset}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>

      {selectedUserId && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4 py-8 bg-black/50" onClick={handleCloseDetail}>
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Карточка пользователя</h2>
              <button onClick={handleCloseDetail} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                <span className="sr-only">Закрыть</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-64px)]">
              {detailLoading ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">Загрузка данных пользователя...</div>
              ) : detailError ? (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                  {detailError}
                </div>
              ) : userDetail ? (
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <img
                      src={userDetail.photoUrl || userDetail.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userDetail.name || userDetail.email)}&background=EEF2FF&color=312E81`}
                      alt={userDetail.name || userDetail.email}
                      className="w-20 h-20 rounded-full object-cover bg-gray-100 dark:bg-gray-700"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userDetail.name || userDetail.email)}&background=EEF2FF&color=312E81`;
                      }}
                    />
                    <div>
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">{userDetail.name || '—'}</h3>
                      <div className="text-gray-600 dark:text-gray-400 space-y-1">
                        <div>Email: {userDetail.email}</div>
                        <div>Telegram: {userDetail.username ? `@${userDetail.username}` : '—'}</div>
                        <div>Telegram ID: {userDetail.telegramId || '—'}</div>
                        <div>Пароль: {userDetail.hasPassword ? 'Установлен' : 'Не установлен'}</div>
                        <div>Создан: {new Date(userDetail.createdAt).toLocaleString('ru-RU')}</div>
                        {userDetail.updatedAt && (
                          <div>Изменён: {new Date(userDetail.updatedAt).toLocaleString('ru-RU')}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-700 rounded-lg p-4">
                      <div className="text-sm text-indigo-700 dark:text-indigo-300">Заказы</div>
                      <div className="text-2xl font-semibold text-indigo-900 dark:text-indigo-100">{userDetail.ordersCount}</div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-700 rounded-lg p-4">
                      <div className="text-sm text-green-700 dark:text-green-300">Выручка</div>
                      <div className="text-2xl font-semibold text-green-900 dark:text-green-100">{userDetail.totalSpent.toLocaleString('ru-RU')} ₽</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-700 rounded-lg p-4">
                      <div className="text-sm text-purple-700 dark:text-purple-300">Последний заказ</div>
                      <div className="text-base font-medium text-purple-900 dark:text-purple-100">
                        {userDetail.lastOrderAt ? new Date(userDetail.lastOrderAt).toLocaleString('ru-RU') : '—'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Последние заказы</h4>
                    {recentOrders.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">Заказы отсутствуют</div>
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                        {recentOrders.map((order) => (
                          <div key={order.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{order.orderNumber}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(order.createdAt).toLocaleString('ru-RU')} · {order.itemsCount} шт.
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-500 dark:text-gray-400">{order.deliveryType === 'pickup' ? 'Самовывоз' : order.deliveryType}</span>
                              <span className="text-sm text-gray-900 dark:text-white font-medium">{order.totalPrice.toLocaleString('ru-RU')} ₽</span>
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  order.status === 'completed'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                    : order.status === 'pending'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                    : order.status === 'cancelled'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {order.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() =>
                        setDeleteTarget({ id: userDetail.id, label: userDetail.name || userDetail.email })
                      }
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      Удалить пользователя
                    </button>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">Данные пользователя отсутствуют</div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4 py-8 bg-black/50" onClick={() => !deleteLoading && setDeleteTarget(null)}>
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Удалить пользователя?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Действие нельзя отменить. Пользователь «{deleteTarget.label}» будет удалён, а его заказы останутся без привязки.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;

